import Image from 'next/image'
import { useState } from 'react'

import { useIntl } from 'react-intl'
import { LuGitPullRequestArrow } from 'react-icons/lu'
import { useShareAssetModalStore } from '@/lib/stores/shareAssetModalStore'

import { routePaths } from '@/lib/core/constants/routes'

import { SummaryCard } from '@/lib/features/Dashboard/components/SummaryCard'
import { StatsBox } from '@/lib/features/Dashboard/components/StatsBox'
import { Details, Row, Summary } from '@/lib/features/Dashboard/Dashboard.styles'
import messages from '@/lib/features/Dashboard/Dashboard.messages'
import { AddAssetModal } from '@/lib/features/Assets/components/AddAssetModal'
import { DashboardStats } from '@/lib/features/Dashboard/api'

import { FeatureTypes } from '@/lib/utils/features'

export type SmartSharesRowProps = {
  onUpdate: (features: Array<FeatureTypes>) => void
  dashboardData: DashboardStats | undefined
  isLoading: boolean
}

export const SmartSharesRow = ({ onUpdate, dashboardData, isLoading }: SmartSharesRowProps) => {
  const { formatMessage } = useIntl()
  const { openModal: openShareAssetModal } = useShareAssetModalStore()
  const [addAssetModalOpen, setAddAssetModalOpen] = useState(false)
  const { smartShares } = messages

  return (
    <>
      <AddAssetModal
        isActive={addAssetModalOpen}
        onClose={(withSuccess) => {
          setAddAssetModalOpen(false)

          if (withSuccess) {
            onUpdate([FeatureTypes.ASSETS, FeatureTypes.SHARES])
          }
        }}
      />

      <Row>
        <Summary>
          <SummaryCard
            callBack={() =>
              openShareAssetModal({
                onSuccess: () => {
                  onUpdate([FeatureTypes.SHARES, FeatureTypes.CONTACTS])
                },
                onAddNewAsset: () => {
                  setAddAssetModalOpen(true)
                },
              })
            }
            color={FeatureTypes.SHARES}
            icon={<LuGitPullRequestArrow size={56} />}
            label={formatMessage(smartShares.allShares)}
          />
        </Summary>
        <Details>
          <StatsBox
            href={routePaths.shareActiveAll}
            color={FeatureTypes.SHARES}
            counter={dashboardData?.shares.allActive ?? 0}
            image={
              <Image
                src="/images/dashboard/smartshares/ss-all.svg"
                alt="Assets"
                width={180}
                height={100}
              />
            }
            isLoading={isLoading}
            label={formatMessage(smartShares.allShares)}
          />
          <StatsBox
            href={routePaths.shareActiveOutbound}
            color={FeatureTypes.SHARES}
            counter={dashboardData?.shares.lent ?? 0}
            image={
              <Image
                src="/images/dashboard/smartshares/ss-all-sharing.svg"
                alt="Assets"
                width={180}
                height={100}
              />
            }
            isLoading={isLoading}
            label={formatMessage(smartShares.sharingVehiclesAndPTUs)}
          />
          <StatsBox
            href={routePaths.shareActiveInbound}
            color={FeatureTypes.SHARES}
            counter={dashboardData?.shares.borrowed ?? 0}
            image={
              <Image
                src="/images/dashboard/smartshares/ss-all-shared.svg"
                alt="Assets"
                width={180}
                height={100}
              />
            }
            isLoading={isLoading}
            label={formatMessage(smartShares.sharedWithMe)}
          />
          <StatsBox
            href={routePaths.shareActiveOnward}
            color={FeatureTypes.SHARES}
            counter={dashboardData?.shares.onward ?? 0}
            image={
              <Image
                src="/images/dashboard/smartshares/ss-onward-shared.svg"
                alt="Assets"
                width={180}
                height={100}
              />
            }
            isLoading={isLoading}
            label={formatMessage(smartShares.sharingOnwards)}
          />
        </Details>
      </Row>
    </>
  )
}
